import type { PropertyFilterProps } from '@risk-smart/themed-cloudscape-components/property-filter';
import PropertyFilter from '@risk-smart/themed-cloudscape-components/property-filter';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

import styles from './style.module.scss';

// Cloudscape's built-in autosuggest filter (see node_modules
// @cloudscape-design/components/property-filter/filter-options.js
// `matchSingleOption`) matches the typed search text against
// `label || value`. For dropdowns like Owner and Tags the `value`
// is a UUID, so typing a digit or short string matches the hex/digit
// content of the UUID — producing a list of unrelated names/tags
// (RSP-5284). Pre-filter the options by label BEFORE handing them to
// Cloudscape so the UUID value can no longer contaminate the match.
//
// Edge case (RSP-5530): when an existing filter token is opened for
// editing, Cloudscape's token editor (token-editor-inputs.js
// `ValueInputAuto`) fires `onLoadItems` with `filteringText` set to
// the existing token's UUID and looks up the matching option in the
// supplied `filteringOptions` to resolve the human-readable label
// (`matchedOption?.label ?? value`). Naïve label-only narrowing drops
// that option, the editor falls back to rendering the raw UUID, and
// the polluted filtering text leaves the value dropdown empty when
// the user starts typing over it. Preserve the option whose value
// exactly matches the current filtering text so the editor can still
// resolve its label without weakening the label-only narrowing for
// genuine typed searches (the UUID is never a typed search itself —
// the user types names, not 36-character hex strings).
const PropertyFilterPanel: FC<PropertyFilterProps> = ({
  filteringOptions,
  onLoadItems,
  ...rest
}) => {
  const [filteringText, setFilteringText] = useState('');
  const [filteringPropertyKey, setFilteringPropertyKey] = useState<
    string | undefined
  >(undefined);

  const visibleOptions = useMemo(() => {
    if (!filteringOptions || !filteringText || !filteringPropertyKey) {
      return filteringOptions;
    }
    const needle = filteringText.toLowerCase();

    return filteringOptions.filter((option) => {
      if (option.propertyKey !== filteringPropertyKey) {
        return true;
      }
      if (option.value === filteringText) {
        return true;
      }
      // Match on label (keeps UUID values out of the match, RSP-5284); fall
      // back to value for auto-derived options that have no label (RSP-5769).
      const haystack = (option.label ?? option.value ?? '').toLowerCase();

      return haystack.includes(needle);
    });
  }, [filteringOptions, filteringText, filteringPropertyKey]);

  return (
    <div className={styles.propertyFilter}>
      <PropertyFilter
        {...rest}
        filteringOptions={visibleOptions}
        onLoadItems={(event) => {
          setFilteringText(event.detail.filteringText ?? '');
          setFilteringPropertyKey(event.detail.filteringProperty?.key);
          onLoadItems?.(event);
        }}
      />
    </div>
  );
};

export default PropertyFilterPanel;
